import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { socialApi } from '../api/social.api'
import type { ReportPayload } from '../types'

export const FRIENDS_KEY = ['social', 'friends'] as const
export const SEARCH_KEY = ['social', 'search'] as const

export function useFriends() {
  return useQuery({
    queryKey: FRIENDS_KEY,
    queryFn: () => socialApi.fetchFriends().then((r) => r.data.data),
  })
}

/**
 * La recherche se fait sur un pseudo exact : tant que le champ est vide, aucune
 * requête n'est envoyée.
 */
export function useUserSearch(username: string) {
  const needle = username.trim()

  return useQuery({
    queryKey: [...SEARCH_KEY, needle],
    queryFn: () => socialApi.searchUsers(needle).then((r) => r.data.data),
    enabled: needle.length > 0,
    staleTime: 30_000,
  })
}

function useFriendMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<unknown>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    // Une invitation change à la fois la liste d'amis et l'état affiché dans les
    // résultats de recherche : les deux caches sont invalidés ensemble.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FRIENDS_KEY })
      void queryClient.invalidateQueries({ queryKey: SEARCH_KEY })
    },
  })
}

export function useSendFriendRequest() {
  return useFriendMutation((userId: string) => socialApi.sendRequest(userId))
}

export function useAcceptFriendRequest() {
  return useFriendMutation((id: string) => socialApi.acceptRequest(id))
}

export function useDeclineFriendRequest() {
  return useFriendMutation((id: string) => socialApi.declineRequest(id))
}

export function useRemoveFriend() {
  return useFriendMutation((userId: string) => socialApi.removeFriend(userId))
}

export function useBlockUser() {
  return useFriendMutation((userId: string) => socialApi.blockUser(userId))
}

export function useReportUser() {
  return useFriendMutation((payload: ReportPayload) => socialApi.reportUser(payload))
}
